import { useState, useMemo } from "react";
import {
  List,
  ListItemText,
  Collapse,
  IconButton,
  TextField,
  Autocomplete,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { ReportListStyles, ReportButtonStyles } from "./reportsPageStyles";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useIsDarkMode } from "store/atoms";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { Close, Search } from "@mui/icons-material";
import {
  brandPrimary70,
  flightBackgroundColor,
  lightTextBody,
} from "constants/colors";
import styled from "styled-components/macro";
import { useKey } from "react-use";
import { camelCase, uniq } from "lodash";
import { DatePickerCalendar } from "components/Dashboard/range-selectors/RangeSelectorToggleable/DateAndShiftPickerMode/DatePickerCalendar";
import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { ReportItemProps } from "./reportPageTypes";
import { useFormatZoned } from "utils/customHooks/genericHooks";
import { useDispatch } from "react-redux";
import { goToDay } from "providers/GlobalStateProvider";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { REPORTS_PAGE_MIN_DATE } from "constants/constants";
import { DateRangePickerStyles } from "components/FlightTrends/DateRange/DateRangePickerStyles";
import { ReportsSummaryMetricsCardControls } from "reports/components/Reports/SummaryMetrics";
import { useLocalStorageState } from "utils/useLocalStorageState";

/** expand/collapsible list of one type of report */
export default function ReportItemAccordion({
  initialExpandState,
  reportTitle,
  reportContent: reportItemsElements,
  shouldExpand = true,
  isInsideExpand = false,
  calendarSelectedDay,
  setCalendarSelectedDay,
}: ReportItemProps) {
  const [isExpanded, setIsExpanded] = useLocalStorageState(
    `accordionExpanded:${reportTitle}`,
    initialExpandState
  );
  const isStorybook = useIsStorybook();

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };
  const [isDarkMode] = useIsDarkMode();

  const [searchText, setSearchText] = useState("");
  const simplifiedSearchText = searchText.toLowerCase().replace(/ /g, "");

  const [selectedReportGroups, setSelectedReportGroups] = useState<string[]>(
    []
  );
  const allReportGroups = useMemo(
    () =>
      uniq(
        reportItemsElements
          ?.reduce((acc, { props }) => {
            return [...acc, ...(props.reportGroups || [])];
          }, [] as string[])
          .filter(Boolean)
      ),
    [reportItemsElements]
  );

  const dispatch = useDispatch();
  const { dayStartMsOffset } = useSettings();
  const formatZoned = useFormatZoned();
  const timeZoneMsOffset = useTimeZoneMsOffset();

  const setSelectedDay = (day: number | null) => {
    if (!day) {
      setCalendarSelectedDay?.(null);
      return;
    }
    const newSelectedDay = {
      range: { ge: day, lt: day + ONE_DAY_OF_MS },
      shift: { name: "" },
    };
    setCalendarSelectedDay?.(newSelectedDay);
    dispatch(
      goToDay({
        newSelectedDay,
        dayStartMsOffset,
        formatZoned,
        shouldGoToFirstShiftInDay: true,
        timeZoneMsOffset,
        shouldGoToCurrentShift: false,
      })
    );
  };
  const allDays = useMemo(
    () =>
      uniq(
        reportItemsElements
          ?.reduce((acc, { props }) => {
            return [...acc, ...(props?.range?.ge ? [props.range.ge] : [])];
          }, [] as number[])
          .filter(Boolean)
      ),
    [reportItemsElements]
  );

  const calendarDayStart = calendarSelectedDay?.range.ge
    ? new Date(calendarSelectedDay.range.ge).setHours(0, 0, 0, 0)
    : null;

  const reportItemsElementsFiltered = reportItemsElements
    // filter by searchText
    ?.filter(({ props }) => {
      const searchableText = `${props.name}-${props.reportScheduleType}`;

      const simplifiedSearchableText = searchableText
        .toLowerCase()
        .replace(/ /g, "");

      return simplifiedSearchableText.includes(simplifiedSearchText);
    })
    // filter by selectedDay
    .filter(
      ({ props }) =>
        !calendarDayStart ||
        !props.range?.ge ||
        // the selected day overlaps with the report's day
        (new Date(props.range.ge).setHours(0, 0, 0, 0) <= calendarDayStart &&
          calendarDayStart <= new Date(props.range.lt).setHours(0, 0, 0, 0))
    )
    // filter by selectedReportGroups
    .map(item => {
      if (selectedReportGroups.length === 0) {
        return item;
      }
      const reportGroupsFiltered = item.props.reportGroups?.filter(group => {
        return selectedReportGroups.includes(group);
      });
      return {
        ...item,
        props: { ...item.props, reportGroups: reportGroupsFiltered },
      };
    });
  if (!reportItemsElements || reportItemsElements?.length === 0) {
    return null;
  }
  return (
    <>
      {shouldExpand ? (
        <ReportListStyles
          {...{ $isInsideExpand: isInsideExpand, $isDarkMode: isDarkMode }}
          className={`reportItemAccordion-${camelCase(reportTitle)}`}
        >
          <ReportButtonStyles className="shiftReports" $isDarkMode={isDarkMode}>
            <IconButton
              className="btnCollapse"
              onClick={handleExpandClick}
              size="large"
            >
              <ExpandMore
                fontSize="large"
                style={{
                  transform: `rotate(${isExpanded ? 0 : -90}deg)`,
                }}
              />
              <ListItemText className="btnLabel" primary={reportTitle} />
            </IconButton>
          </ReportButtonStyles>
          {!isStorybook && (
            <Collapse in={isExpanded} timeout={1000} unmountOnExit={true}>
              {reportItemsElements &&
              reportItemsElements.length * allReportGroups.length >= 5 ? (
                <ReportsFiltersStyles>
                  <ReportsSummaryMetricsCardControls />
                  <ReportGroupFiltersAutocomplete
                    {...{
                      allReportGroups,
                      selectedReportGroups,
                      setSelectedReportGroups,
                    }}
                  />
                  <SearchInput {...{ searchText, setSearchText }} />
                  {allDays.length > 0 ? (
                    <DatePickerInput
                      {...{
                        selectedDay: calendarSelectedDay?.range.ge ?? null,
                        setSelectedDay,
                        allDays,
                      }}
                    />
                  ) : null}
                </ReportsFiltersStyles>
              ) : null}
              <List id="shiftReportsList">{reportItemsElementsFiltered}</List>
            </Collapse>
          )}
        </ReportListStyles>
      ) : (
        <ReportButtonStyles className="shiftReports" $isDarkMode={isDarkMode}>
          <List id="shiftReportsList" className="btnLabel">
            {reportItemsElements}
          </List>
        </ReportButtonStyles>
      )}
    </>
  );
}

const ReportsFiltersStyles = styled.div`
  display: flex;
  gap: 16px;
  width: -webkit-fill-available;
  padding: 18px 24px 0px;
  .MuiInputBase-root {
    width: 100%;
  }
  input.MuiInputBase-input {
    padding: 8px 0;
  }
  position: relative;
`;

function DatePickerInput({
  selectedDay,
  setSelectedDay,
  allDays,
}: {
  selectedDay: number | null;
  setSelectedDay:
    | React.Dispatch<React.SetStateAction<number | null>>
    | ((ge: number | null) => void);
  allDays: number[];
}) {
  const [open, setOpen] = useState(false);
  useKey("Escape", () => {
    setOpen(false);
    setSelectedDay(null);
  });
  const { translate } = useFlightTranslation();
  const reportConfig = useReportConfig();
  const eosReportTime = reportConfig?.startTime ?? REPORTS_PAGE_MIN_DATE;
  const [isDarkMode] = useIsDarkMode();
  return (
    <DateRangePickerStyles
      $step={null}
      $isClockOpen={false}
      $isVerticalLayout={false}
      $isDarkMode={isDarkMode}
    >
      <DatePickerStyles
        $active={Boolean(selectedDay)}
        $open={open}
        onClick={() => setOpen(true)}
        className={selectedDay ? undefined : "empty"}
      >
        <DatePickerCalendar
          open={open}
          setOpen={setOpen}
          onChange={(nextDate: Date) =>
            setSelectedDay(nextDate ? nextDate.getTime() : null)
          }
          reactDatePickerGe={selectedDay ? new Date(selectedDay) : null}
          minDate={new Date(Math.min(...allDays, eosReportTime))}
          maxDate={new Date(Math.max(...allDays, Date.now()))}
        />
        {selectedDay ? null : (
          <div className={`placeholder${open ? " active" : ""}`}>
            {translate("Filter by date")}
          </div>
        )}
      </DatePickerStyles>
    </DateRangePickerStyles>
  );
}
const DatePickerStyles = styled.div<{ $open: boolean; $active: boolean }>`
  z-index: 1;
  &.empty {
    .react-date-picker__clear-button,
    .react-date-picker__inputGroup__input,
    .react-date-picker__inputGroup__divider {
      visibility: hidden;
    }
  }
  .react-date-picker__wrapper {
    display: flex;
    height: 100%;
    width: fit-content;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.23);
  }
  .react-date-picker__calendar-button {
    border: none;
    cursor: pointer;
  }
  .react-date-picker__calendar-button__icon {
    margin-bottom: -2px;
  }
  cursor: pointer;
  &:hover {
    .react-date-picker__wrapper {
      border-color: rgba(0, 0, 0, 0.87);
    }
    .react-date-picker__calendar-button__icon {
      stroke: ${brandPrimary70};
    }
  }
  .datePickerButton {
    height: 40px;
  }
  .datePickerCalendar {
    max-width: unset;
  }
  .react-date-picker__inputGroup {
    display: ${({ $open, $active }) => ($open || $active ? "flex" : "none")};
    align-items: center;
    padding-bottom: 2px;
  }
  .react-date-picker__inputGroup__input,
  .react-date-picker__inputGroup {
    font-size: 16px;
  }
  .react-date-picker__inputGroup__input,
  .react-date-picker__clear-button {
    background: none;
    border: none;
  }
  .react-date-picker__clear-button {
    margin-bottom: -2px;
    cursor: pointer;
  }
  .react-date-picker__wrapper {
    border-radius: 4px;
    border-color: rgba(0, 0, 0, 0.23);
    padding-left: 8px;
    padding-right: 4px;
    box-sizing: border-box;
    min-width: 190px !important;
    max-width: 190px !important;
    height: 39px;
    display: flex;
    justify-content: right;
  }
  position: relative;
  .placeholder {
    pointer-events: none;
    color: #58595b;
    position: absolute;
    left: 12px;
    top: 15px;
    bottom: 0;
    display: flex;
    align-items: center;
    transition: all 0.3s;
    z-index: 1;
    height: fit-content;
    padding: 0 4px;
    transform: translateY(-4px);
  }
  .placeholder.active {
    transform: translate(-2px, -22px) scale(0.75);
    background: ${flightBackgroundColor};
    transform-origin: top left;
  }
  .react-date-picker__inputGroup__leadingZero {
    height: fit-content !important;
    width: 6px;
  }
  .react-date-picker__inputGroup__divider {
    height: 16px !important;
    width: 7px;
    margin-left: -1px;
    margin-right: 1px;
  }
`;

function ReportGroupFiltersAutocomplete({
  allReportGroups,
  selectedReportGroups,
  setSelectedReportGroups,
}) {
  const { translate } = useFlightTranslation();
  useKey("Escape", () => {
    setSelectedReportGroups([]);
  });
  return (
    <StyledAutocomplete
      className={`groupInput ${
        selectedReportGroups.length > 0 ? "active" : ""
      }`}
      options={["", ...allReportGroups]}
      value={selectedReportGroups.join(", ")}
      renderInput={params => (
        <TextField
          {...params}
          label={translate("Filter by group")}
          variant="outlined"
        />
      )}
      onChange={(_, value) => {
        // for now only allow one group to be selected
        setSelectedReportGroups(value ? [value] : []);
      }}
    />
  );
}
const StyledAutocomplete = styled(Autocomplete)`
  .MuiInputBase-root {
    padding: 0;
    padding-bottom: 1px;
  }
  &:not(.Mui-focused):not(.active) {
    .MuiInputLabel-root {
      transform: translate(14px, 8px) scale(1);
    }
  }
  &&& input.MuiAutocomplete-input {
    min-width: 160px;
    padding-left: 12px;
  }
`;
function SearchInput({ searchText, setSearchText }) {
  const { translate } = useFlightTranslation();
  useKey("Escape", () => {
    setSearchText("");
  });
  return (
    <StyledTextField
      className={`searchInput${searchText ? " active" : ""}`}
      label={translate("Filter by name")}
      InputProps={{
        startAdornment: (
          <Search
            style={{
              width: 20,
              height: 20,
              marginRight: 10,
              color: lightTextBody,
            }}
          />
        ),
        endAdornment: searchText ? (
          <IconButton
            className="btnClearInput"
            onClick={() => {
              setSearchText("");
            }}
          >
            <Close />
          </IconButton>
        ) : null,
        style: { opacity: 1 },
      }}
      value={searchText ?? ""}
      onChange={e => {
        setSearchText(e.target.value);
      }}
    />
  );
}
const StyledTextField = styled(TextField)`
  .btnClearInput {
    border: none;
    padding: 0;
    margin-right: -6px;
    color: ${lightTextBody};
  }
`;
